/**
 * @FIXME(shawk):
 * Until an upstream dependency issue in "eslint-plugin-react" is resolved,
 * disabling eslint rule "react/prop-types" is the recommended approach
 * to resolve false positives in eslint.
 *
 * see:
 * - https://github.com/yannickcr/eslint-plugin-react/issues/3140
 * - https://github.com/redwoodjs/redwood/pull/3762
 */

import cx from 'classnames';
import React from 'react';
import { Collapse } from 'react-collapse';
import { TFunction } from 'i18next';
import { FieldLabel, HelperText } from 'components/Forms';
import { CheckboxInput } from 'components/Forms/inputs/CheckboxInput';
import { ConnectedFormInputProps } from 'types/form-field';
import type { Option } from 'components/Forms';

type Size = 'sm' | 'md' | 'lg';

export type Props = {
  /**
   * Checkbox options to display
   */
  options: Option[];

  /**
   * Render prop to show when options are empty. Is displayed between the label
   * and helper text/error message.
   */
  renderEmpty?: () => JSX.Element;

  /**
   * Disables the display of error messages in this component. Should be used only
   * if the error is otherwise displayed.
   */
  suppressError?: boolean;
  size?: Size;

  tPrefixKey?: string;
  tPrefixDescriptionKey?: string;
  tFunc?: TFunction;
} & ConnectedFormInputProps<HTMLInputElement>;

export const CheckboxGroup = React.forwardRef<HTMLInputElement, Props>(function CheckboxGroupWithRef(
  {
    name,
    label,
    labelClassName,
    accessibleLabel,
    error,
    hint,
    options,
    tPrefixDescriptionKey,
    suppressError = false,
    renderEmpty,
    setError,
    setValue,
    clearValue,
    autoFocus,
    ...inputProps
  },
  ref?: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  return (
    <fieldset>
      <FieldLabel as="legend" className={labelClassName} accessibleLabel={accessibleLabel}>
        {label}
      </FieldLabel>

      {hint && (
        <div className="-mt-8 md:-mt-12">
          <HelperText>{hint}</HelperText>
        </div>
      )}

      <Collapse isOpened theme={{ collapse: 'transition-all duration-300' }}>
        <div className="mt-4 md:mt-8 mb-10 md:mb-14 space-y-12 md:space-y-16 font-dazzed">
          {options.map((option, index) => {
            const id = `${name}-${option.value}`;

            return (
              <CheckboxInput
                {...inputProps}
                autoFocus={autoFocus && index === 0}
                ref={ref}
                id={id}
                key={id}
                name={name}
                option={option}
                hasError={!!error}
                tPrefixDescriptionKey={tPrefixDescriptionKey}
              />
            );
          })}

          {options.length === 0 && renderEmpty ? renderEmpty() : null}
        </div>
      </Collapse>

      {error && (
        <HelperText error className={cx({ 'sr-only': suppressError })}>
          {error}
        </HelperText>
      )}
    </fieldset>
  );
});
